/**
 * Aftercare → Report tab (T-209): the numbers and call notes Paul presents for a
 * week or month. Pure helpers; the tab is AftercareReport.tsx and the calls come
 * from the get_aftercare_report_calls RPC (20261030150900_aftercare_report_calls.sql).
 */

/**
 * A call counts as connected when the talk time (answered → hang-up, ringing
 * excluded) is at least this long. Paul's voicemails run 40–57 s of talk time
 * (greeting plus his message), real conversations start around 55–60 s. At 60 s,
 * 11 of Paul's 22 October calls to aftercare families count as connected.
 */
export const AFTERCARE_CONNECTED_MIN_TALK_SECONDS = 60;

/** Aftercare emails go out from Paul's address (AftercareService.sendAftercareEmail). */
export const AFTERCARE_EMAIL_SENDER = 'paul@cleocremation.com';
export const DEFAULT_AFTERCARE_STAFF = AFTERCARE_EMAIL_SENDER;

const TIME_ZONE = 'America/Toronto';

export type PeriodKey = 'this_week' | 'last_week' | 'this_month' | 'last_month';

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
];

export interface Period {
  key: PeriodKey;
  /** Inclusive, ISO instant of local (ET) midnight. */
  start: string;
  /** Exclusive, ISO instant of local (ET) midnight. */
  end: string;
  /** "Week of Oct 19–25, 2026" / "October 2026". */
  label: string;
}

export interface ReportCall {
  communication_id: string;
  occurred_at: string;
  direction: 'outbound' | 'inbound';
  agent_email: string | null;
  agent_name: string | null;
  connected: boolean;
  funeral_id: number;
  region: string | null;
  deceased_name: string | null;
  family_name: string | null;
  summary: string | null;
}

interface YMD {
  y: number;
  m: number; // 1-12
  d: number;
}

const etDate = (instant: Date): YMD => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
};

/** Offset of ET from UTC at `instant`, in ms (negative: ET is behind). */
const etOffsetMs = (instant: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(instant / 1000) * 1000;
};

/** The UTC instant of midnight ET on a calendar date (month/day may overflow). */
const etMidnight = ({ y, m, d }: YMD): Date => {
  const guess = Date.UTC(y, m - 1, d);
  let instant = guess - etOffsetMs(guess);
  // Re-check at the result: the offset can differ across a DST change.
  instant = guess - etOffsetMs(instant);
  return new Date(instant);
};

/** Calendar arithmetic on a date with no time zone. */
const addDays = ({ y, m, d }: YMD, days: number): YMD => {
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const weekLabel = (first: YMD, last: YMD): string => {
  const a = `${MONTHS[first.m - 1]} ${first.d}`;
  const b = first.m === last.m ? `${last.d}` : `${MONTHS[last.m - 1]} ${last.d}`;
  return `Week of ${a}–${b}, ${last.y}`;
};

/** Weeks start Monday, months on the 1st, both at midnight Eastern. */
export const periodFor = (key: PeriodKey, now: Date = new Date()): Period => {
  const today = etDate(now);
  if (key === 'this_week' || key === 'last_week') {
    const dow = new Date(Date.UTC(today.y, today.m - 1, today.d)).getUTCDay(); // 0 = Sunday
    let monday = addDays(today, -((dow + 6) % 7));
    if (key === 'last_week') monday = addDays(monday, -7);
    const nextMonday = addDays(monday, 7);
    return {
      key,
      start: etMidnight(monday).toISOString(),
      end: etMidnight(nextMonday).toISOString(),
      label: weekLabel(monday, addDays(monday, 6)),
    };
  }
  const offset = key === 'last_month' ? -1 : 0;
  const first = new Date(Date.UTC(today.y, today.m - 1 + offset, 1));
  const ymd = { y: first.getUTCFullYear(), m: first.getUTCMonth() + 1, d: 1 };
  return {
    key,
    start: etMidnight(ymd).toISOString(),
    end: etMidnight({ y: ymd.y, m: ymd.m + 1, d: 1 }).toISOString(),
    label: `${MONTHS_LONG[ymd.m - 1]} ${ymd.y}`,
  };
};

/** "Oct 20" in Eastern time. */
export const formatCallDate = (iso: string): string => {
  const { m, d } = etDate(new Date(iso));
  return `${MONTHS[m - 1]} ${d}`;
};

export const caseLabel = (call: Pick<ReportCall, 'funeral_id' | 'region'>): string => {
  const suffix = call.region === 'ontario' ? ' ON' : call.region === 'quebec' ? ' QC' : '';
  return `#${call.funeral_id}${suffix}`;
};

/**
 * Aircall's transcription mangles the brand ("Clio Cremation", "Cléoclémation",
 * "Cleocrénation"); the report is pasted as-is, so put it back to "Cleo".
 */
const normaliseBrand = (text: string): string =>
  text
    .replace(/\bCl[ée]?[iéeo]{1,2}\s?-?\s?[ck][rl][ée][mn]ation\b/gi, 'Cleo')
    .replace(/\bClio\b/g, 'Cleo');

/** First two sentences of the call summary, at most ~280 characters. */
export const shortSummary = (summary: string | null | undefined, maxChars = 280): string | null => {
  const text = summary?.replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const sentences = text.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g) ?? [text];
  let out = sentences.slice(0, 2).join(' ').replace(/\s+/g, ' ').trim();
  if (out.length > maxChars) out = `${out.slice(0, maxChars - 1).trimEnd()}…`;
  return normaliseBrand(out);
};

export interface StaffOption {
  email: string;
  name: string;
}

/** Everyone who called an aftercare family in the period; the default staff is always listed. */
export const staffOptions = (calls: ReportCall[], defaultName = 'Paul Simard'): StaffOption[] => {
  const byEmail = new Map<string, string>([[DEFAULT_AFTERCARE_STAFF, defaultName]]);
  for (const c of calls) {
    if (!c.agent_email) continue;
    const email = c.agent_email.toLowerCase();
    if (!byEmail.has(email) || (email === DEFAULT_AFTERCARE_STAFF && c.agent_name)) {
      byEmail.set(email, c.agent_name || email);
    }
  }
  return [...byEmail.entries()]
    .map(([email, name]) => ({ email, name }))
    .sort((a, b) =>
      a.email === DEFAULT_AFTERCARE_STAFF ? -1 : b.email === DEFAULT_AFTERCARE_STAFF ? 1 : a.name.localeCompare(b.name),
    );
};

export interface ReportNumbers {
  /** Aftercare emails sent in the period; null when the staff is not the aftercare sender. */
  emailsSent: number | null;
  /** Outbound calls to aftercare families, connected or not. */
  callsMade: number;
  /** Outbound calls with talk time >= AFTERCARE_CONNECTED_MIN_TALK_SECONDS. */
  callsConnected: number;
  /** Inbound calls from aftercare families this staff answered, same talk-time bar. */
  familyCallsAnswered: number;
  /** Every connected conversation (outbound + inbound), oldest first. */
  connected: ReportCall[];
}

export const reportNumbers = (
  calls: ReportCall[],
  staffEmail: string,
  emailsSent: number | null,
): ReportNumbers => {
  const mine = calls.filter((c) => (c.agent_email ?? '').toLowerCase() === staffEmail.toLowerCase());
  const outbound = mine.filter((c) => c.direction === 'outbound');
  const connected = mine
    .filter((c) => c.connected)
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  return {
    emailsSent: staffEmail.toLowerCase() === AFTERCARE_EMAIL_SENDER ? emailsSent : null,
    callsMade: outbound.length,
    callsConnected: outbound.filter((c) => c.connected).length,
    familyCallsAnswered: connected.filter((c) => c.direction === 'inbound').length,
    connected,
  };
};

export const NO_SUMMARY = 'No summary recorded.';

/** Plain text for "Copy for report". Never includes a call duration. */
export const buildReportText = (args: {
  staffName: string;
  period: Period;
  numbers: ReportNumbers;
}): string => {
  const { staffName, period, numbers } = args;
  const lines = [
    `Aftercare report: ${staffName}, ${period.label}`,
    '',
    ...(numbers.emailsSent !== null ? [`Emails sent: ${numbers.emailsSent}`] : []),
    `Calls made: ${numbers.callsMade}`,
    `Calls connected: ${numbers.callsConnected}`,
    ...(numbers.familyCallsAnswered ? [`Calls from families answered: ${numbers.familyCallsAnswered}`] : []),
  ];
  if (numbers.connected.length) {
    lines.push('', 'Conversations:');
    for (const c of numbers.connected) {
      const who = c.family_name ? `${c.family_name} (${caseLabel(c)})` : caseLabel(c);
      const calledIn = c.direction === 'inbound' ? ' (they called)' : '';
      lines.push(`- ${formatCallDate(c.occurred_at)}, ${who}${calledIn}: ${shortSummary(c.summary) ?? NO_SUMMARY}`);
    }
  }
  return lines.join('\n');
};
